import React, { useEffect, useState, useMemo } from 'react';
import { Box, ThemeProvider, createTheme, CssBaseline, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useABTest, useABTestConversion } from '../../hooks/useABTest';
import { getStudentLandingThemeOverrides } from './styles/landingThemeConfig';
import { landingAnalytics } from './services/landingAnalytics';
import { links } from '../../config/links';

// Components
import HeroWithDemo from './components/HeroWithDemo';
import StudentAdvantages from './components/StudentAdvantages';
import StudentFAQ from './components/StudentFAQ';
import StudentCTA from './components/StudentCTA';
import MinimalFooter from './components/MinimalFooter';
import EmailCaptureModal from './components/EmailCaptureModal';
import EducatorEmailCaptureModal from './components/EducatorEmailCaptureModal';
import DevThemeSwitcher from './components/DevThemeSwitcher';

const StudentLandingPage = () => {
  const { } = useAppTheme(); // darkMode available if needed
  const { trackPageView, trackEvent } = useAnalytics();
  const { trackConversion } = useABTestConversion();
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0); // Track scroll progress for animation
  const [navEmailModalOpen, setNavEmailModalOpen] = useState(false);
  const [educatorModalOpen, setEducatorModalOpen] = useState(false);
  const [colorTheme, setColorTheme] = useState(() => {
    // Load saved theme from localStorage in dev mode
    if (process.env.NODE_ENV === 'development') {
      return localStorage.getItem('devColorTheme') || 'teal';
    }
    return 'teal';
  }); // Theme state
  
  // Handle theme change
  const handleThemeChange = (newTheme) => {
    setColorTheme(newTheme);
    // Save to localStorage in dev mode
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('devColorTheme', newTheme);
    }
  };
  
  // A/B testing for CTA button text (shared with Hero)
  const { variant: ctaVariant } = useABTest('landing_cta', [
    'variant_a', // Get Early Access
    'variant_b'  // Join the Student Waitlist
  ]);

  const getCTAText = () => {
    if (ctaVariant === 'variant_b') {
      return "Join the Student Waitlist";
    }
    return "Get Early Access";
  };

  // Create a custom theme for the student landing page with selected color theme
  const theme = useMemo(
    () => createTheme(getStudentLandingThemeOverrides(colorTheme)),
    [colorTheme]
  );

  useEffect(() => {
    trackPageView('Student Landing Page');
    
    // Initialize enhanced analytics tracking
    landingAnalytics.initScrollTracking();
    
    // Track page load time after a short delay
    setTimeout(() => {
      landingAnalytics.trackPageLoadTime();
    }, 100);
    
    // Apply themed gradient background to body
    const originalBodyStyle = document.body.style.cssText;
    const studentTheme = theme.palette;
    
    // Create a subtle gradient background using theme colors
    document.body.style.background = `
      radial-gradient(ellipse 150% 100% at 50% -20%, ${studentTheme.primary.main}15 0%, transparent 45%),
      radial-gradient(ellipse 100% 60% at 20% 100%, ${studentTheme.primary.main}08 0%, transparent 40%),
      radial-gradient(ellipse 80% 50% at 80% 120%, ${studentTheme.accent}05 0%, transparent 50%),
      linear-gradient(180deg, ${studentTheme.background.default} 0%, ${studentTheme.background.paper}80 50%, ${studentTheme.background.default} 100%)
    `;
    document.body.style.minHeight = '100vh';
    document.body.style.backgroundColor = studentTheme.background.default;
    
    // Cleanup on unmount
    return () => {
      document.body.style.cssText = originalBodyStyle;
    };
  }, [trackPageView]);

  // Scroll listener for navigation animation
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const triggerPoint = 200; // Full transition at 200px
      
      // Calculate scroll progress from 0 to 1
      const progress = Math.min(scrollPosition / triggerPoint, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation handlers
  const handleLogin = () => {
    trackEvent('Sticky Nav Click', { action: 'Login' });
    navigate(links.internal.login);
  };

  const handleSignUp = () => {
    trackEvent('cta_click', { 
      source: 'nav',
      cta_variant: ctaVariant
    });
    trackConversion('landing_cta', ctaVariant, 'nav_click');
    setNavEmailModalOpen(true);
  };

  const handleEducatorClick = () => {
    trackEvent('Sticky Nav Click', { action: 'Educator Modal' });
    setEducatorModalOpen(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Main wrapper - no background, transparent */}
      <Box sx={{ 
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        backgroundColor: 'transparent',
      }}>
      
      {/* Dev Theme Switcher */}
      <DevThemeSwitcher currentTheme={colorTheme} onThemeChange={handleThemeChange} />
      
      {/* Top Navigation - Always visible with animated transition */}
      <Box
        sx={{
          position: 'fixed',
          top: scrollProgress > 0 ? 16 * scrollProgress : 0, // Animate down from top
          left: 0,
          right: 0,
          zIndex: 10,
          display: 'flex',
          justifyContent: 'center',
          px: { xs: 2, sm: 3, md: 4 },
          transition: 'all 0.3s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            maxWidth: scrollProgress === 0 ? '100%' : `${1400 - (scrollProgress * 200)}px`, // Start at full width, then animate
            px: scrollProgress === 0 ? 0 : { xs: 2, sm: 3, md: 4 }, // No padding initially, add as scrolling
            py: 2 - (scrollProgress * 0.5), // Slightly reduce padding as scroll
            backgroundColor: scrollProgress > 0.1 ? 'rgba(26, 26, 46, 0.7)' : 'transparent',
            backdropFilter: scrollProgress > 0.1 ? 'blur(20px)' : 'none',
            borderRadius: scrollProgress * 6, // Animate border radius (doubled)
            border: scrollProgress > 0.1 ? `1px solid rgba(0, 212, 170, ${0.2 * scrollProgress})` : 'none',
            boxShadow: scrollProgress > 0.1 ? `0 ${8 * scrollProgress}px ${32 * scrollProgress}px rgba(0, 0, 0, ${0.4 * scrollProgress})` : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          {/* Logo - hidden on mobile */}
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 700,
              fontSize: '1.25rem',
              background: 'linear-gradient(45deg, #ffffff 30%, #00d4aa 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            examr
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <Button
              color="inherit"
              onClick={handleEducatorClick}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                },
              }}
            >
              For Educators
            </Button>
            <Button
              color="inherit"
              onClick={handleLogin}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Login
            </Button>
            <Button
              variant="contained"
              onClick={handleSignUp}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              {getCTAText()}
            </Button>
          </Box>
        </Box>
      </Box>
      

      {/* Content wrapper - ensure transparent */}
      <Box 
        sx={{ 
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'transparent',
        }}
      >
        <HeroWithDemo colorTheme={colorTheme} />
        <StudentAdvantages colorTheme={colorTheme} />
        <StudentFAQ colorTheme={colorTheme} />
        <Box id="waitlist-section">
          <StudentCTA colorTheme={colorTheme} />
        </Box>
        <MinimalFooter colorTheme={colorTheme} onEducatorClick={() => setEducatorModalOpen(true)} />
      </Box>
      
      {/* Email Capture Modal for Navigation */}
      <EmailCaptureModal
        open={navEmailModalOpen}
        onClose={() => setNavEmailModalOpen(false)}
        source="nav"
        headlineVariant="nav" // Navigation doesn't have headline A/B test
        ctaVariant={ctaVariant}
        colorTheme={colorTheme}
      />
      
      {/* Educator Email Capture Modal */}
      <EducatorEmailCaptureModal
        open={educatorModalOpen}
        onClose={() => setEducatorModalOpen(false)}
        colorTheme={colorTheme}
      />
      </Box>
    </ThemeProvider>
  );
};

export default StudentLandingPage;